import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ShieldCheck, MapPin, MessageSquare, Heart, GitCompare } from 'lucide-react';
import { Button } from './ui/button';
import EnquiryModal from './EnquiryModal';
import { useData } from '../context/DataContext';
import { inr } from '../lib/api';
import { wishlist, compare } from '../lib/storage';
import { useToast } from '../hooks/use-toast';

const ProductCard = ({ product }) => {
  const { supplierById } = useData();
  const { toast } = useToast();
  const supplier = supplierById?.[product.supplier_id];
  const [enquiryOpen, setEnquiryOpen] = useState(false);
  const [saved, setSaved] = useState(() => wishlist.has(product.id));
  const [inCompare, setInCompare] = useState(() => compare.has(product.id));

  const toggleSave = () => { const r = wishlist.toggle(product.id); setSaved(r); toast({ title: r ? 'Added to wishlist' : 'Removed from wishlist' }); };
  const toggleCompare = () => {
    const r = compare.toggle(product.id);
    if (r.error) toast({ title: r.error, variant: 'destructive' });
    else { setInCompare(!inCompare); toast({ title: inCompare ? 'Removed from compare' : 'Added to compare' }); }
  };

  return (
    <>
      <div className="bg-white border border-gray-200 rounded-md overflow-hidden hover:shadow-md transition-shadow flex flex-col relative">
        <div className="absolute top-2 right-2 z-10 flex flex-col gap-1">
          <button onClick={toggleSave} title="Add to wishlist"
            className={`w-7 h-7 rounded-full flex items-center justify-center transition-colors ${
              saved ? 'bg-rose-100 text-rose-600' : 'bg-white/90 text-gray-500 hover:text-rose-600'
            }`}>
            <Heart className={`w-3.5 h-3.5 ${saved ? 'fill-rose-600' : ''}`} />
          </button>
          <button onClick={toggleCompare} title="Add to compare"
            className={`w-7 h-7 rounded-full flex items-center justify-center transition-colors ${
              inCompare ? 'bg-blue-100 text-[#2c54b2]' : 'bg-white/90 text-gray-500 hover:text-[#2c54b2]'
            }`}>
            <GitCompare className="w-3.5 h-3.5" />
          </button>
        </div>
        <Link to={`/product/${product.slug || product.id}`} className="block aspect-square overflow-hidden bg-gray-50">
          <img
            src={product.image}
            alt={product.name}
            className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
            loading="lazy"
          />
        </Link>
        <div className="p-3 flex flex-col flex-1">
          <Link to={`/product/${product.slug || product.id}`} className="text-sm font-medium text-gray-900 hover:text-[#ee5e23] line-clamp-2 mb-2 transition-colors">
            {product.name}
          </Link>
          <div className="flex items-baseline gap-1 mb-2">
            <span className="text-lg font-bold text-[#2c54b2]">{inr(product.price)}</span>
            <span className="text-xs text-gray-500">/ {product.unit}</span>
          </div>
          <div className="text-xs text-gray-600 mb-1 flex items-center gap-1">
            <ShieldCheck className="w-3.5 h-3.5 text-green-600" />
            <span className="font-medium">{product.gst}</span>
          </div>
          {supplier && (
            <Link to={`/vendor/${supplier.slug || supplier.id}`} className="text-xs text-gray-700 hover:text-[#2c54b2] mb-1 truncate transition-colors">
              {supplier.name}
            </Link>
          )}
          {supplier && (
            <div className="text-xs text-gray-500 flex items-center gap-1 mb-3">
              <MapPin className="w-3 h-3" />
              <span className="truncate">{supplier.city}</span>
            </div>
          )}
          <div className="mt-auto">
            <Button
              onClick={() => setEnquiryOpen(true)}
              className="w-full bg-[#ee5e23] hover:bg-[#d24e15] text-white text-xs h-8 px-2"
              data-testid={`product-contact-${product.id}`}
            >
              <MessageSquare className="w-3.5 h-3.5 mr-1 shrink-0" /> <span className="truncate">Contact</span>
            </Button>
          </div>
        </div>
      </div>
      <EnquiryModal open={enquiryOpen} onOpenChange={setEnquiryOpen} product={product} />
    </>
  );
};

export default ProductCard;
